import React, { FC, useContext } from "react";
import {
  IntlShape,
  MessageDescriptor,
  defineMessages,
  useIntl,
} from "react-intl";
import { useNavigate } from "react-router-dom";
import { Button } from "@natera/form";

import { PostBillAPIContext, IntlContext, Language } from "@app/provider";
import { routes } from "@app/routing";
import { getFormattedLocalizeDate } from "@app/utils";
import { PastDueType } from "@app/postBill/models";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { usePostBillHeapEvent } from "@app/postBill/hooks";
import { convertBalance } from "@app/postBill/utils/convertBalance";
import { HEAP_EVENTS } from "@app/provider/types";
import {
  GetItemsResponseData,
  PaymentDueStatus,
  ReviewAndPayStatus,
} from "@app/postBill/models/apiControllerModel";
import { getLimsClinicIds, getTestsOrdered } from "@app/postBill/utils";

import "./billingSummaryCard.scss";

const messages = defineMessages({
  totalDueTitle: {
    id: "totalDueTitle",
    defaultMessage: "Total balance ",
  },
  totalDueTestCount: {
    id: "totalDueTestCount",
    defaultMessage: "({testCount})",
  },
  billingSummaryTestTextSingular: {
    id: "billingSummaryTestTextSingular",
    defaultMessage: "test",
  },
  billingSummaryTestTextPlural: {
    id: "billingSummaryTestTextPlural",
    defaultMessage: "tests",
  },
  paymentDueDate: {
    id: "paymentDueDate",
    defaultMessage: "Payment due on {dueDate}",
  },
  paymentDueDateDueUponReceipt: {
    id: "paymentDueDateDueUponReceipt",
    defaultMessage: "Payment due upon receipt",
  },
  paymentDueDateDueNow: {
    id: "paymentDueDateDueNow",
    defaultMessage: "Payment due now",
  },
  lastPaymentDate: {
    id: "lastPaymentDate",
    defaultMessage: "Last payment received on {lastPaymentDate}",
  },
  lastPaymentPendingDate: {
    id: "lastPaymentPendingDate",
    defaultMessage:
      "Last payment received on {lastPaymentDate}. It may take a few days for the balance to update.",
  },
  noPaymentDueText: {
    id: "noPaymentDueText",
    defaultMessage: "You don’t have a payment due right now",
  },
  reviewButton: {
    id: "reviewButton",
    defaultMessage: "Review and pay",
  },
  billingHistoryButton: {
    id: "billingHistoryButton",
    defaultMessage: "View payment history",
  },
  billingPastDueDateDueNow: {
    id: "billingPastDueDateDueNow",
    defaultMessage: "Your bill is past due. Pay your bill now",
  },
  billingPreCollectionNoticeDueNow: {
    id: "billingPreCollectionNoticeDueNow",
    defaultMessage: "Your bill is in pre-collection. Pay your bill now",
  },
});

const BillingSummaryCard: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    apiProviderState: { billingData },
  } = useContext(PostBillAPIContext);
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();
  const { currentLanguage } = useContext(IntlContext);

  const handleReviewAndPay = () => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_click_billing_payment_review,
      extraData: {
        tests_ordered: getTestsOrdered(billingData?.readyToPay),
        lims_clinic_id: getLimsClinicIds(billingData?.readyToPay),
      },
    });
    navigate(routes.reviewBills);
  };

  const handleViewPaymentHistory = () => {
    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_click_viewpaymenthistory,
    });
    navigate(routes.billingHistoryPayments);
  };

  return (
    <div className="total-cost-container">
      <div
        className={`total-cost-container__title ${
          billingData && getPastDueExists(billingData) ? "error" : ""
        }`}
      >
        {getTitleText(intl, billingData)}
      </div>
      {getBalanceContent(intl, billingData)}
      {getDateContent(intl, currentLanguage, billingData)}
      {getBillingSummaryCardButton(
        intl,
        billingData,
        handleReviewAndPay,
        handleViewPaymentHistory
      )}
    </div>
  );
};

function getPastDueExists(data: GetItemsResponseData): boolean {
  return data.pastDueExists && data.readyToPay.length > 0;
}

function getItemCount(data: GetItemsResponseData): number {
  return data.readyToPay.length;
}

function getNumItemsText(intl: IntlShape, data: GetItemsResponseData): string {
  if (data.balanceDateStatus === ReviewAndPayStatus.NOTHING_TO_PAY) return "";

  const itemsCount = getItemCount(data);

  if (itemsCount === 1) {
    return `${itemsCount} ${intl.formatMessage(
      messages.billingSummaryTestTextSingular
    )}`;
  }

  return `${itemsCount} ${intl.formatMessage(
    messages.billingSummaryTestTextPlural
  )}`;
}

function getTitleText(
  intl: IntlShape,
  data: GetItemsResponseData | undefined
): string {
  if (!data) return "";

  const totalDueTitle = intl.formatMessage(messages.totalDueTitle);
  const testCountText = ` (${getNumItemsText(intl, data)})`;

  return `${totalDueTitle}${getItemCount(data) > 0 ? testCountText : ""}`;
}

function getBalanceContent(
  intl: IntlShape,
  data: GetItemsResponseData | undefined
) {
  if (!data) return null;

  const showBalance =
    data.balanceDateStatus !== ReviewAndPayStatus.NOTHING_TO_PAY;

  if (showBalance === false) {
    return (
      <div className="total-cost-container__no-payment">
        {intl.formatMessage(messages.noPaymentDueText)}
      </div>
    );
  }

  const currency =
    data.readyToPay[0]?.currency ?? data.readyToPayInstallments[0]?.currency;

  return (
    <div
      className={`total-cost-container__price ${
        getPastDueExists(data) ? "error" : ""
      }`}
    >
      {convertBalance(data.totalBalance, true, currency)}
    </div>
  );
}

function getDateContentClassName(data: GetItemsResponseData): string {
  if (getPastDueExists(data)) return "total-cost-container__status error";

  return "total-cost-container__status";
}

function getDateContentTextNothingToPay(
  intl: IntlShape,
  currentLanguage: Language,
  data: GetItemsResponseData
): string {
  if (data.displayDate.pastDueExists !== PaymentDueStatus.VALID_DATE) return "";

  return intl.formatMessage(messages.lastPaymentDate, {
    lastPaymentDate: getFormattedLocalizeDate(
      data?.displayDate.dueDate,
      currentLanguage,
      true
    ),
  });
}

const PaymentDateObjectMap: Record<
  PastDueType,
  Record<PaymentDueStatus, MessageDescriptor | undefined>
> = {
  [PastDueType.FINAL_NOTICE]: {
    [PaymentDueStatus.VALID_DATE]: messages.billingPastDueDateDueNow,
    [PaymentDueStatus.DUE_UPON_RECEIPT]: messages.billingPastDueDateDueNow,
    [PaymentDueStatus.DUE_NOW]: messages.billingPastDueDateDueNow,
    [PaymentDueStatus.EMPTY]: undefined,
  },
  [PastDueType.PAST_DUE]: {
    [PaymentDueStatus.VALID_DATE]: messages.billingPastDueDateDueNow,
    [PaymentDueStatus.DUE_UPON_RECEIPT]: messages.billingPastDueDateDueNow,
    [PaymentDueStatus.DUE_NOW]: messages.billingPastDueDateDueNow,
    [PaymentDueStatus.EMPTY]: undefined,
  },
  [PastDueType.PRE_COLLECTION_NOTICE]: {
    [PaymentDueStatus.VALID_DATE]: messages.billingPreCollectionNoticeDueNow,
    [PaymentDueStatus.DUE_UPON_RECEIPT]:
      messages.billingPreCollectionNoticeDueNow,
    [PaymentDueStatus.DUE_NOW]: messages.billingPreCollectionNoticeDueNow,
    [PaymentDueStatus.EMPTY]: undefined,
  },
  [PastDueType.NO_PAST_DUE]: {
    [PaymentDueStatus.VALID_DATE]: messages.paymentDueDate,
    [PaymentDueStatus.DUE_UPON_RECEIPT]: messages.paymentDueDateDueUponReceipt,
    [PaymentDueStatus.DUE_NOW]: messages.paymentDueDateDueNow,
    [PaymentDueStatus.EMPTY]: undefined,
  },
};

type PaymentTextParams = {
  dueDate?: string;
};

function getDateContextTextDueDate(
  intl: IntlShape,
  currentLanguage: Language,
  data: GetItemsResponseData
): string {
  const messageType =
    PaymentDateObjectMap[data.pastDueType][data.displayDate.pastDueExists];

  if (!messageType) return "";

  const messageParams: PaymentTextParams = {};
  if (data.displayDate.pastDueExists === PaymentDueStatus.VALID_DATE) {
    messageParams.dueDate = getFormattedLocalizeDate(
      data?.displayDate.dueDate,
      currentLanguage,
      true
    );
  }

  return intl.formatMessage(messageType, messageParams);
}

function getDateContextTextPaymentPendingsOnly(
  intl: IntlShape,
  currentLanguage: Language,
  data: GetItemsResponseData
): string {
  if (data.displayDate.pastDueExists !== PaymentDueStatus.VALID_DATE) {
    return "";
  }

  return intl.formatMessage(messages.lastPaymentPendingDate, {
    lastPaymentDate: getFormattedLocalizeDate(
      data?.displayDate.dueDate,
      currentLanguage,
      true
    ),
  });
}

function getDateContentText(
  intl: IntlShape,
  currentLanguage: Language,
  data: GetItemsResponseData
): string {
  switch (data.balanceDateStatus) {
    case ReviewAndPayStatus.NOTHING_TO_PAY:
      return getDateContentTextNothingToPay(intl, currentLanguage, data);

    case ReviewAndPayStatus.PAYMENTS_WAITING:
      return getDateContextTextDueDate(intl, currentLanguage, data);

    case ReviewAndPayStatus.PAYMENT_PENDINGS_ONLY:
      return getDateContextTextPaymentPendingsOnly(intl, currentLanguage, data);

    default:
      return "";
  }
}

function getDateContent(
  intl: IntlShape,
  currentLanguage: Language,
  data: GetItemsResponseData | undefined
) {
  if (!data) return null;

  if (data.displayDate.pastDueExists === PaymentDueStatus.EMPTY) return null;

  const dateContentText = getDateContentText(intl, currentLanguage, data);
  if (dateContentText.length === 0) return null;

  return <div className={getDateContentClassName(data)}>{dateContentText}</div>;
}

function getReviewAndPayButton(intl: IntlShape, callback: () => void) {
  return (
    <ButtonNoShadow type="button" raised className="button" onClick={callback}>
      {intl.formatMessage(messages.reviewButton)}
    </ButtonNoShadow>
  );
}

function getPaymentHistoryButton(intl: IntlShape, callback: () => void) {
  return (
    <Button type="button" outlined className="button" onClick={callback}>
      {intl.formatMessage(messages.billingHistoryButton)}
    </Button>
  );
}

function getBillingSummaryCardButton(
  intl: IntlShape,
  data: GetItemsResponseData | undefined,
  reviewAndPayCallback: () => void,
  paymentHistoryCallback: () => void
) {
  if (!data) return;

  // If there are waiting payments, we definitely need to display the Review & Pay button
  if (data.balanceDateStatus === ReviewAndPayStatus.PAYMENTS_WAITING) {
    return getReviewAndPayButton(intl, reviewAndPayCallback);
  }

  // If there is nothing to pay (it could be NOTHIN_TO_PAY or PENDING_PAYMENTS_ONLY, doesn't matter),
  // we are not going to display the Review & Pay button. Now the question is, do we display the
  // payment history button?
  // Unless balanceDateStatus is PAYMENTS_WAITING, backend will send the latest payment date in
  // data.displayDate.dueDate. So if this date exists, it means there are previous payments to see,
  // so we display the payment history button. If the dueDate doesn't exist, there is nothing to pay,
  // and there are no previous payments. We don't display the button in that case.
  if (data.displayDate.dueDate) {
    return getPaymentHistoryButton(intl, paymentHistoryCallback);
  }
}

export default BillingSummaryCard;
